/**
 * DataPrepPreview Component
 * 
 * Shows a preview of the transformed data with:
 * - Column headers with type indicators
 * - First N rows of data
 * - Null value highlighting
 * - Column stats on hover
 */

import React, { useMemo } from 'react';
import { Hash, Type, Calendar, ToggleLeft, EyeOff, AlertTriangle, BarChart2 } from 'lucide-react';
import type { ColumnSchema } from '../../services/dataPrepTypes';

interface DataPrepPreviewProps {
    data: Record<string, any>[];
    schema: ColumnSchema[];
    maxRows?: number;
    isPreviewMode?: boolean;
    totalRows?: number;
}

const typeIcons: Record<string, React.FC<{ className?: string }>> = {
    string: Type,
    number: Hash,
    date: Calendar,
    boolean: ToggleLeft
};

const typeColors: Record<string, string> = {
    string: 'text-blue-400',
    number: 'text-green-400',
    date: 'text-purple-400',
    boolean: 'text-orange-400'
};

function formatCellValue(value: any, type: string): string {
    if (value === null || value === undefined || value === '') {
        return '—';
    }
    if (type === 'number' && typeof value === 'number') {
        return Number.isInteger(value) ? value.toString() : value.toFixed(2);
    }
    if (type === 'date' && value) {
        try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString();
            }
        } catch { }
    }
    return String(value);
}

export const DataPrepPreview: React.FC<DataPrepPreviewProps> = ({
    data,
    schema,
    maxRows = 50,
    isPreviewMode = false,
    totalRows
}) => {
    const visibleSchema = useMemo(
        () => schema.filter(col => !col.hidden),
        [schema]
    );

    const displayData = useMemo(
        () => data.slice(0, maxRows),
        [data, maxRows]
    );

    if (visibleSchema.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                No columns to display
            </div>
        );
    }

    return (
        <div className="relative">
            {/* Preview Mode Banner */}
            {isPreviewMode && (
                <div className="flex items-center gap-2 px-3 py-2 bg-yellow-500/10 border-b border-yellow-500/20 text-yellow-400 text-xs">
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>
                        Preview mode: showing first {data.length.toLocaleString()} of {totalRows?.toLocaleString() ?? '?'} rows
                    </span>
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs bg-white/5 text-gray-300 uppercase sticky top-0 z-10 backdrop-blur-md">
                        <tr>
                            <th className="px-2 py-2 text-gray-500 font-normal w-12 text-center">#</th>
                            {visibleSchema.map(col => {
                                const Icon = typeIcons[col.type] || Type;
                                const colorClass = typeColors[col.type] || 'text-gray-400';
                                const hasWarnings = col.warnings.length > 0;

                                return (
                                    <th
                                        key={col.name}
                                        className="px-3 py-2 font-medium tracking-wider whitespace-nowrap group relative"
                                    >
                                        <div className="flex items-center gap-1.5">
                                            <Icon className={`w-3.5 h-3.5 ${colorClass}`} />
                                            <span className="truncate max-w-[150px]" title={col.name}>
                                                {col.name}
                                            </span>
                                            {hasWarnings && (
                                                <AlertTriangle className="w-3 h-3 text-yellow-500" />
                                            )}
                                        </div>

                                        {/* Stats Tooltip */}
                                        <div className="absolute left-0 top-full mt-1 hidden group-hover:block z-20">
                                            <div className="bg-gray-900 border border-white/10 rounded-lg p-3 shadow-xl min-w-[200px]">
                                                <div className="text-sm font-medium text-gray-200 mb-2">{col.name}</div>
                                                <div className="space-y-1 text-xs">
                                                    <div className="flex justify-between text-gray-400">
                                                        <span>Type:</span>
                                                        <span className={colorClass}>{col.type}</span>
                                                    </div>
                                                    <div className="flex justify-between text-gray-400">
                                                        <span>Nulls:</span>
                                                        <span className={col.stats.nullCount > 0 ? 'text-yellow-400' : 'text-gray-500'}>
                                                            {col.stats.nullCount}
                                                        </span>
                                                    </div>
                                                    <div className="flex justify-between text-gray-400">
                                                        <span>Unique:</span>
                                                        <span>{col.stats.uniqueCount}</span>
                                                    </div>
                                                    {col.type === 'number' && col.stats.mean !== undefined && (
                                                        <>
                                                            <div className="flex justify-between text-gray-400">
                                                                <span>Mean:</span>
                                                                <span>{col.stats.mean.toFixed(2)}</span>
                                                            </div>
                                                            <div className="flex justify-between text-gray-400">
                                                                <span>Min/Max:</span>
                                                                <span>{col.stats.min} / {col.stats.max}</span>
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                                {col.warnings.length > 0 && (
                                                    <div className="mt-2 pt-2 border-t border-white/10">
                                                        {col.warnings.map((w, i) => (
                                                            <div key={i} className="text-xs text-yellow-400 flex items-center gap-1">
                                                                <AlertTriangle className="w-3 h-3" />
                                                                {w}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                        {displayData.map((row, rowIndex) => (
                            <tr
                                key={rowIndex}
                                className="hover:bg-white/5 transition-colors"
                            >
                                <td className="px-2 py-2 text-gray-600 text-xs text-center">
                                    {rowIndex + 1}
                                </td>
                                {visibleSchema.map(col => {
                                    const value = row[col.name];
                                    const isNull = value === null || value === undefined || value === '';
                                    const displayValue = formatCellValue(value, col.type);

                                    return (
                                        <td
                                            key={col.name}
                                            className={`px-3 py-2 truncate max-w-[200px] ${isNull ? 'text-gray-600 italic' : 'text-gray-300'
                                                }`}
                                            title={isNull ? 'null' : String(value)}
                                        >
                                            {displayValue}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Row Count Footer */}
            {data.length > maxRows && (
                <div className="text-center py-2 text-xs text-gray-500 border-t border-white/5">
                    Showing {maxRows} of {data.length.toLocaleString()} rows
                </div>
            )}
        </div>
    );
};

export default DataPrepPreview;
